import { useMemo, useState } from "react";
import { KLayout, KDemo } from "@/pages/kreonis/KLayout";
import { Seo } from "@/components/Seo";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip, ReferenceLine } from "recharts";

export default function KBacktest() {
  const [asset, setAsset] = useState("BTC/USD");
  const [years, setYears] = useState(3);
  const [risk, setRisk] = useState(1);
  const data = useMemo(() => {
    let s = 0; for (const c of asset + years + risk) s = (s * 31 + c.charCodeAt(0)) % 9973;
    const rand = () => ((s = (s * 1103515245 + 12345) % 2147483648) / 2147483648);
    let eq = 100000; const arr = [{ i: 0, equity: eq }];
    for (let i = 1; i <= 120; i++) { eq *= 1 + (rand() - 0.46) * risk * 0.012; arr.push({ i, equity: Math.round(eq) }); }
    return arr;
  }, [asset, years, risk]);
  const net = ((data[data.length - 1].equity - 100000) / 1000).toFixed(1);
  return (
    <KLayout>
      <Seo title="KREONIS — Backtest Fidelity" />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="flex items-center justify-between"><h1 className="font-display text-3xl font-bold text-slate-50">Historical Model Fidelity Testing</h1><KDemo /></div>
        <p className="mt-2 text-slate-400">Backtest Fidelity / Performance Analytics — past performance does not guarantee future results.</p>
        <div className="mt-6 grid gap-6 lg:grid-cols-4">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-3 text-sm">
            <div><label className="mb-1 block text-[10px] uppercase text-slate-500">Asset</label><select value={asset} onChange={(e) => setAsset(e.target.value)} data-testid="kbt-asset" className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-200"><option>BTC/USD</option><option>ETH/USD</option><option>EUR/USD</option><option>S&P 500</option><option>Gold</option></select></div>
            <div><label className="mb-1 block text-[10px] uppercase text-slate-500">Historical Period: {years}y</label><input type="range" min="1" max="3" value={years} onChange={(e) => setYears(+e.target.value)} className="w-full" /></div>
            <div><label className="mb-1 block text-[10px] uppercase text-slate-500">Risk / Allocation: {risk}%</label><input type="range" min="1" max="3" step="1" value={risk} onChange={(e) => setRisk(+e.target.value)} data-testid="kbt-risk" className="w-full" /></div>
          </div>
          <div className="lg:col-span-3 space-y-6">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[["Net Return", `${net}%`], ["Model Fidelity", "78.4%"], ["Max Drawdown", "-11.2%"], ["Profit Factor", "1.74"]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-slate-800 bg-slate-900/50 p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className="mt-1 font-display text-lg font-bold text-slate-100">{v}</div></div>
              ))}
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5" data-testid="kbt-curve">
              <h3 className="mb-3 font-display text-lg font-semibold text-slate-100">Equity Curve</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data}>
                  <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11, fill: "#64748b" }} width={60} />
                  <XAxis dataKey="i" hide />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`$${v.toLocaleString()}`, "Equity"]} />
                  <ReferenceLine y={100000} stroke="#334155" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </KLayout>
  );
}
